import { Directive, ElementRef, HostListener, inject, input, Input } from '@angular/core';

@Directive({
    selector: '[appCopy]',
    standalone: true,
})
export class CopyDirective {
    /**
     * Reference to the host element for tooltip positioning.
     */
    private el = inject(ElementRef);

    /**
     * The text content to copy to the clipboard.
     */
    textToCopy = input<string>('', { alias: 'appCopy' });

    @HostListener('click', ['$event'])
    async copyText(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.textToCopy()) return;

        try {
            // Modern API
            await navigator.clipboard.writeText(this.textToCopy());
            this.showTooltip(event);
        } catch {
            // Fallback for older browsers
            this.fallbackCopy(this.textToCopy());
            this.showTooltip(event);
        }
    }

    /**
     * Shows a "Copied!" tooltip near the clicked position.
     */
    private showTooltip(event: MouseEvent): void {
        const tooltip = document.createElement('div');
        tooltip.textContent = 'Copied!';
        tooltip.style.cssText = `
            position: fixed;
            top: ${event.clientY - 35}px;
            left: ${event.clientX}px;
            transform: translateX(-50%);
            background: #1e293b;
            color: white;
            padding: 6px 12px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 500;
            z-index: 9999;
            pointer-events: none;
            animation: fadeInOut 1.5s ease forwards;
        `;

        // Add animation keyframes
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeInOut {
                0% { opacity: 0; transform: translateX(-50%) translateY(5px); }
                15% { opacity: 1; transform: translateX(-50%) translateY(0); }
                85% { opacity: 1; transform: translateX(-50%) translateY(0); }
                100% { opacity: 0; transform: translateX(-50%) translateY(-5px); }
            }
        `;
        document.head.appendChild(style);
        document.body.appendChild(tooltip);

        // Remove tooltip after animation
        setTimeout(() => {
            tooltip.remove();
            style.remove();
        }, 1500);
    }

    /**
     * Fallback method to copy text to clipboard.
     * @param text The text to copy to the clipboard.
     */
    private fallbackCopy(text: string) {
        const textarea = document.createElement('textarea');
        textarea.value = text;

        // Avoid scrolling to bottom
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';

        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    }
}
